import { useState } from 'react';
import { Badge, Button } from '@indipia/ui-core';
import { statusBadgeTone } from '@indipia/shared-features';
import { PhotoLightbox } from '../PhotoLightbox';
import { ThumbImg } from '../ThumbImg';
import type { CustReqDelivered } from '../../types/custReqWorkOrder.types';
import { CUST_REQ_ORDER_MESSAGE as M, reqMetaText } from '../../utils/custReqOrder.logic';

/**
 * 작업요청이력 카드의 '고객 요청 n ▸'(941 S16-9) — 전달 당시 요청 목록(원문 · 경로 · 접수일)과 지금 상태(전달됨 · 반영됨 · 확인).
 * 요청 원문은 인디공사현황에서 고칠 수 없다(읽기 전용). 상태 이름은 서버가 준 statusName 을 쓴다.
 */

/** 공종 태그 옆 토글 버튼 — 요청이 없으면 그리지 않는다 */
export function CustReqToggleButton({ count, open, onToggle }: { count: number; open: boolean; onToggle: () => void }) {
  if (count <= 0) return null;
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      aria-expanded={open}
      onClick={onToggle}
    >
      {M.listToggle(count)} {open ? '▾' : '▸'}
    </Button>
  );
}

/** 펼침 목록 */
export function WorkOrderCustReqList({ items }: { items: CustReqDelivered[] }) {
  const [lightbox, setLightbox] = useState<{ item: number; photo: number } | null>(null);
  if (items.length === 0) return null;
  const lbItem = lightbox ? items[lightbox.item] : null;

  return (
    <div className="mx-05 mb-03 rounded-lg border border-border bg-surface p-04">
      <ul className="flex flex-col gap-03">
        {items.map((it, i) => (
          <li key={it.custReqId} className="list-none">
            <div className="flex items-start gap-sm">
              <Badge status={statusBadgeTone(it.statusCode)}>{it.statusName}</Badge>
              <div className="min-w-0 flex-1">
                <p className="text-body-sm leading-relaxed text-text" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {it.content}
                </p>
                <div className="mt-01 text-caption text-text-muted">{reqMetaText(it.srcName, it.receivedAt, it.photos.length)}</div>
                {it.photos.length > 0 && (
                  <div className="mt-02 flex flex-wrap gap-sm">
                    {it.photos.map((p, pi) => (
                      <button
                        key={p.photoId}
                        type="button"
                        aria-label={`고객 요청 ${i + 1} 사진 ${pi + 1} 크게보기`}
                        onClick={() => setLightbox({ item: i, photo: pi })}
                        className="h-10 w-10 shrink-0 overflow-hidden rounded-md bg-surface-muted p-0"
                      >
                        <ThumbImg url={p.thumbUrl} alt="" className="h-full w-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </li>
        ))}
      </ul>

      <PhotoLightbox
        photos={(lbItem?.photos ?? []).map((p) => ({ url: p.url }))}
        index={lightbox && lbItem ? lightbox.photo : null}
        onClose={() => setLightbox(null)}
        onIndexChange={(pi) => setLightbox((cur) => (cur ? { item: cur.item, photo: pi } : cur))}
        renderImage={(photo, style) => <ThumbImg url={photo.url} alt="" imgStyle={style} />}
      />
    </div>
  );
}
